import { useMutation, useQueryClient } from "react-query";

import { AppToaster } from "@components/AppToaster";
import { useActions } from "@hooks/useActions";
import { useOrg } from "@hooks/useOrg";
import { CRUDOperationType } from "@types";
import { optimisticCreate, optimisticDelete, optimisticUpdate } from "@utils";

import { createAction, deleteAction, updateAction } from "@pages/settings/api";
import { Action, ActionResponse } from "@pages/settings/types";

const checkNameAlreadyExists = (
  oldActions: Action[],
  modifiedAction: Action
) => {
  const found = oldActions.find(
    (action) =>
      action.name === modifiedAction.name && action.id !== modifiedAction.id
  );
  return found !== undefined;
};

/**
 *
 * @param operation - CRUD operation to be performed by the mutation
 * @returns a use mutation result, which can be used to perform mutations with .mutate
 *
 * Not exported - this encapsulates all the CRUD logic for actions.
 */
const useActionMutation = (operation: CRUDOperationType) => {
  const queryClient = useQueryClient();
  const org = useOrg();
  const { data: oldActions } = useActions();

  if (!org) throw new Error("No org");

  let operationFunction: (action: Action) => Promise<ActionResponse>;

  if (operation === "create") {
    operationFunction = (action: Action) => {
      return createAction(org.id, action);
    };
  } else if (operation === "update") {
    operationFunction = (action: Action) => {
      if (action.id === undefined) {
        throw new Error(`Cannot update action with undefined id`);
      }
      return updateAction(org.id, action.id, action);
    };
  } else if (operation === "delete") {
    operationFunction = (action: Action) => {
      if (action.id === undefined) {
        throw new Error(`Cannot delete action with undefined id`);
      }
      return deleteAction(org.id, action.id);
    };
  } else {
    throw new Error(`Invalid mutation operation "${operation}" on action`);
  }

  return useMutation(operationFunction, {
    onMutate: async (action: Action): Promise<{ oldActions?: Action[] }> => {
      if (checkNameAlreadyExists(oldActions ?? [], action)) {
        throw new Error("Actions must have a unique name");
      }

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(["actions", { org }]);

      // optimistically update
      if (operation === "create") {
        queryClient.setQueriesData(["actions", { org }], (old?: Action[]) =>
          optimisticCreate(old, action)
        );
      } else if (operation === "update") {
        queryClient.setQueriesData(["actions", { org }], (old?: Action[]) =>
          optimisticUpdate(old, action, "id")
        );
      } else if (operation === "delete") {
        queryClient.setQueriesData(["actions", { org }], (old?: Action[]) =>
          optimisticDelete(old, action, "id")
        );
      }

      // return context object with snapshotted value in case of error
      return { oldActions };
    },
    onSuccess: (action) => {
      AppToaster.show({
        message: `Successfully ${operation}d ${action.name}`,
        intent: "success",
      });
      queryClient.invalidateQueries(["actions", { org }]);
    },
    onError: (error, action, context) => {
      // rollback optimistic update
      if (context?.oldActions) {
        queryClient.setQueriesData(["actions", { org }], context.oldActions);
      }

      AppToaster.show({
        message: `Error ${operation.slice(0, -1)}ing ${action.name}: ${error}`,
        intent: "danger",
      });
    },
  });
};

/**
 * Mutation hook that creates an action. Pass arguments to create an action to .mutate
 * Shows AppToaster on success or error
 * Invalidates actions query so it will be refetched
 */
export const useCreateAction = () => useActionMutation("create");

/**
 * Mutation hook that updates an action. Pass arguments to update an action to .mutate
 * Shows AppToaster on success or error
 * Invalidates actions query so it will be refetched
 */
export const useUpdateAction = () => useActionMutation("update");

/**
 * Mutation hook that deletes an action. Pass arguments to delete an action to .mutate
 * Shows AppToaster on success or error
 * Invalidates actions query so it will be refetched
 */
export const useDeleteAction = () => useActionMutation("delete");
